'use client';

import { PlotlyChart } from './PlotlyChart';
import type { MetricsSample } from '../types';
import { sortPods } from '../ChartContext';

interface Props {
  samples: MetricsSample[];
  selectField: (s: MetricsSample) => number | null | undefined;
  yLabel?: string;
  yRange?: [number, number];
  shapes?: import('plotly.js').Layout['shapes'];
  height?: number;
  loading?: boolean;
  legendSortDirection?: import('../chart-legend').ChartLegendSortDirection;
}

export function MetricsChart({
  samples,
  selectField,
  yLabel,
  yRange,
  shapes,
  height,
  loading,
  legendSortDirection,
}: Props) {
  const pods = sortPods(Array.from(new Set(samples.map((s) => s.pod))));
  const byPod = new Map<string, { x: Date[]; y: number[] }>();
  for (const pod of pods) byPod.set(pod, { x: [], y: [] });
  for (const s of [...samples].sort((a, b) => a.ts_ms - b.ts_ms)) {
    const v = selectField(s);
    if (v == null) continue;
    const series = byPod.get(s.pod);
    if (!series) continue;
    series.x.push(new Date(s.ts_ms));
    series.y.push(v);
  }

  const traces = pods.map((pod) => {
    const series = byPod.get(pod)!;
    return {
      x: series.x,
      y: series.y,
      type: 'scatter' as const,
      mode: 'lines' as const,
      name: pod,
      connectgaps: false,
    };
  });

  return (
    <PlotlyChart
      traces={traces}
      yTitle={yLabel}
      yRange={yRange}
      shapes={shapes}
      height={height}
      loading={loading}
      legendSortDirection={legendSortDirection}
    />
  );
}
